import { Parallel } from './Parallel.js';

/** @import { ClientOrAdminFirebase } from './types/ClientOrAdminFirebase.js' */
/** @import ClientFirebase from 'firebase/compat/app' */

const companyNamesCache = new Map();
const inFlightRequests = new Map();

/**
 * Get company names for a set of company IDs. Accepts either an array of company ID strings or an object whose keys are company IDs.
 * @param {Record<string, unknown>|string[]} companyIds
 * @param {{ companyNames?: Record<string, string>, firebaseApp?: ClientOrAdminFirebase }} [options]
 * @returns {Promise<Record<string, string>>}
 */
export async function GetCompanyNames(companyIds, options = {}) {
  const { companyNames = {}, firebaseApp = /** @type {ClientFirebase} */ (globalThis.FirebaseWorker) } = options;
  const companyIdsList = Array.isArray(companyIds) ? companyIds : Object.keys(companyIds);
  const database = firebaseApp.database();

  await Parallel.forEach(
    companyIdsList,
    async (companyId) => {
      // Prefer passed-in names, then previously fetched module cache, then firebase
      if (typeof companyNames[companyId] === 'string' && companyNames[companyId] !== '') return;
      if (companyNamesCache.has(companyId)) {
        companyNames[companyId] = companyNamesCache.get(companyId);
        return;
      }

      if (inFlightRequests.has(companyId)) {
        companyNames[companyId] = await inFlightRequests.get(companyId);
        return;
      }

      // If we don't already have a name for this company then get it from Firebase.
      try {
        const inFlightRequest = database
          .ref(`photoheight/companies/${companyId}/name`)
          .once('value')
          .then((snapshot) => snapshot.val() || companyId);
        inFlightRequests.set(companyId, inFlightRequest);

        const companyName = await inFlightRequest;
        companyNamesCache.set(companyId, companyName);
        companyNames[companyId] = companyName;
      } finally {
        inFlightRequests.delete(companyId);
      }
    },
    10
  );
  return companyNames;
}
